import {
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type SyntheticEvent,
} from 'react'

import { CommandItem } from '@/components/ui/command'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { usePermissions } from '@/hooks/use-permissions'
import { cn } from '@/lib/utils'

interface ActionGateProps {
  /** The org the permission is checked against — see usePermissions. */
  organizationId: string
  /** Permission key, e.g. "InvoicesManage". Server-expanded, membership-only. */
  permission: string
  /** The user's task word, never the permission's internal verb. */
  verb: 'create' | 'edit' | 'delete' | 'send' | 'manage' | (string & {})
  /** Plural domain word an event planner uses: "invoices", "team members". */
  noun: string
  /** A single interactive element: Button, DropdownMenuItem, etc. */
  children: ReactElement
}

/**
 * Single source of truth for the denied-action tooltip copy. The second
 * sentence never varies, so users learn the escape hatch once.
 */
export function permissionDeniedMessage(verb: string, noun: string): string {
  return `You don't have permission to ${verb} ${noun}. An admin can grant access from the Team page.`
}

/** Disabled *look* without the disabled *semantics* — see the note on ActionGate. */
const DISABLED_CLASSNAME = 'cursor-not-allowed opacity-50'

/**
 * Children that render their disabled state from their *own* `disabled` prop
 * and write it *after* spreading the caller's props, so an injected
 * `aria-disabled` is silently discarded. cmdk's `Item` is the case in hand: it
 * destructures `disabled`/`onSelect` out and then emits
 * `aria-disabled={!!disabled}` and `onClick={select}` after `{...rest}`, so
 * cloning `aria-disabled` produced `aria-disabled="false"` on a control that
 * looked disabled, and cloning `onClick` was thrown away entirely.
 *
 * For these, `disabled` is the only disable the component honours: cmdk then
 * drops the item from keyboard navigation (it selects only
 * `[cmdk-item]:not([aria-disabled="true"])`), never registers its select
 * listener, and renders no click handler. We add `tabIndex` so the tooltip is
 * still reachable, since a cmdk item is a `div` with no natural tab stop.
 */
const OWNS_DISABLED_STATE: ReadonlySet<unknown> = new Set([CommandItem])

/**
 * Swallow a child's activation. Called with whatever the child passes: cmdk
 * hands `onSelect` the item's *string* value rather than an event, so this
 * must not assume it received one — it used to throw
 * "event.preventDefault is not a function" on the first click of a denied item.
 */
function suppressEvent(eventOrValue?: unknown) {
  const event = eventOrValue as Partial<SyntheticEvent> | undefined
  if (typeof event?.preventDefault !== 'function') return
  event.preventDefault()
  event.stopPropagation?.()
}

/**
 * Write-permission gate: the control stays visible so the capability is
 * discoverable, but is disabled and explains itself on hover or focus.
 *
 * Permitted → `children` renders untouched: no wrapper element, no tooltip,
 * no extra DOM. A fully-permissioned user sees an identical app.
 *
 * Denied → the child is cloned with `aria-disabled`, disabled styling and
 * swallowed activation, and wrapped in a tooltip.
 *
 * **Why not native `disabled`:** Radix tooltips never fire on elements with
 * `pointer-events: none` (which shadcn's Button sets while disabled), and a
 * natively-disabled button drops out of the tab order — so the explanation
 * would be unreachable by hover *and* by keyboard, which is the entire point
 * of the control. `aria-disabled` keeps the element focusable and hoverable
 * while still announcing "disabled" to assistive tech. When the caller has
 * *already* natively disabled the child for its own reason (a pending
 * mutation, say), we keep that and wrap it in a focusable `<span>` so the
 * tooltip still has a trigger.
 *
 * **Suppression is not just an `onClick` prop.** A child is free to run its own
 * handler before ours, to replace ours outright, or to call ours with
 * something that is not an event, and two of those shipped as controls that
 * looked disabled and acted anyway. So the denial is enforced three ways:
 * `asChild` is unwrapped (below), the click is caught in the capture phase
 * before any handler the child attached, and `OWNS_DISABLED_STATE` children
 * get the native `disabled` their framework actually honours.
 */
export function ActionGate({ organizationId, permission, verb, noun, children }: ActionGateProps) {
  const { can, isLoading } = usePermissions(organizationId)

  if (can(permission)) return children

  const childProps = (children.props ?? {}) as {
    asChild?: boolean
    children?: ReactNode
    className?: string
    disabled?: boolean
  }

  // `asChild` hands rendering to a grandchild — in practice a router Link. A
  // denied control goes nowhere, so it has no business *being* a link: Radix's
  // Slot merges our handler behind the grandchild's own (`mergeProps` chains
  // child-first), and the rendered `<a href>` stays reachable by middle-click
  // and "Open link in new tab" whatever we do to `onClick`. Rendering the
  // trigger as itself — a real `<button>` — removes the destination entirely.
  const slotted =
    childProps.asChild && isValidElement(childProps.children)
      ? (childProps.children as ReactElement<{ children?: ReactNode; className?: string }>)
      : null

  const disabledChild = cloneElement(children as ReactElement<Record<string, unknown>>, {
    ...(slotted && { asChild: false, children: slotted.props.children }),
    'aria-disabled': true,
    className: cn(childProps.className, slotted?.props.className, DISABLED_CLASSNAME),
    // Capture runs before any handler the child attached to itself, however it
    // composed the props we handed it.
    onClickCapture: suppressEvent,
    onClick: suppressEvent,
    onSelect: suppressEvent,
    ...(OWNS_DISABLED_STATE.has(children.type) && { disabled: true, tabIndex: 0 }),
  })

  // Fail closed while permissions are still resolving (the _app prefetch means
  // this is rare): the control holds its space so there is no layout shift and
  // no flash of a forbidden capability, but we don't assert a denial yet.
  if (isLoading) return disabledChild

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {childProps.disabled ? (
          <span
            tabIndex={0}
            className="inline-flex rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            {disabledChild}
          </span>
        ) : (
          disabledChild
        )}
      </TooltipTrigger>
      <TooltipContent>{permissionDeniedMessage(verb, noun)}</TooltipContent>
    </Tooltip>
  )
}
